
export interface IpInfo {
  ip: string;
  country: string;
  countryCode: string;
}

export async function getIpInfo(): Promise<IpInfo> {
  try {
    // Using ipify API for IP detection (free tier)
    const ipResponse = await fetch('https://api.ipify.org/?format=json');
    const ipData = await ipResponse.json();
    
    // Using ipapi.co for country detection (free tier)
    const locationResponse = await fetch(`https://ipapi.co/${ipData.ip}/json/`);
    const locationData = await locationResponse.json();
    
    return {
      ip: ipData.ip,
      country: locationData.country_name || 'Unknown',
      countryCode: locationData.country_code || 'US'
    };
  } catch (error) {
    console.error('Failed to fetch IP info:', error);
    return {
      ip: 'Unknown',
      country: 'Unknown',
      countryCode: 'US'
    };
  }
}

// Map of country codes to their time zones - liste complète
export const countryTimezones = {
  'US': ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Anchorage', 'America/Adak', 'America/Phoenix', 'America/Indiana/Indianapolis', 'America/Detroit', 'Pacific/Honolulu'],
  'CA': ['America/Vancouver', 'America/Edmonton', 'America/Regina', 'America/Winnipeg', 'America/Toronto', 'America/Halifax', 'America/St_Johns', 'America/Yellowknife', 'America/Whitehorse', 'America/Iqaluit'],
  'MX': ['America/Mexico_City', 'America/Tijuana', 'America/Hermosillo', 'America/Mazatlan', 'America/Chihuahua', 'America/Monterrey', 'America/Merida', 'America/Cancun'],
  'BR': ['America/Sao_Paulo', 'America/Manaus', 'America/Rio_Branco', 'America/Belem', 'America/Fortaleza', 'America/Recife', 'America/Noronha'],
  'AR': ['America/Argentina/Buenos_Aires', 'America/Argentina/Cordoba', 'America/Argentina/Salta', 'America/Argentina/Jujuy', 'America/Argentina/Tucuman', 'America/Argentina/Catamarca', 'America/Argentina/La_Rioja', 'America/Argentina/San_Juan', 'America/Argentina/Mendoza', 'America/Argentina/San_Luis', 'America/Argentina/Rio_Gallegos', 'America/Argentina/Ushuaia'],
  'GB': ['Europe/London'],
  'IE': ['Europe/Dublin'],
  'PT': ['Europe/Lisbon', 'Atlantic/Azores', 'Atlantic/Madeira'],
  'ES': ['Europe/Madrid', 'Atlantic/Canary', 'Africa/Ceuta'],
  'FR': ['Europe/Paris'],
  'BE': ['Europe/Brussels'],
  'NL': ['Europe/Amsterdam'],
  'DE': ['Europe/Berlin', 'Europe/Busingen'],
  'AT': ['Europe/Vienna'],
  'CH': ['Europe/Zurich'],
  'IT': ['Europe/Rome'],
  'VA': ['Europe/Vatican'],
  'SM': ['Europe/San_Marino'],
  'MT': ['Europe/Malta'],
  'DK': ['Europe/Copenhagen'],
  'NO': ['Europe/Oslo'],
  'SE': ['Europe/Stockholm'],
  'FI': ['Europe/Helsinki'],
  'EE': ['Europe/Tallinn'],
  'LV': ['Europe/Riga'],
  'LT': ['Europe/Vilnius'],
  'PL': ['Europe/Warsaw'],
  'CZ': ['Europe/Prague'],
  'SK': ['Europe/Bratislava'],
  'HU': ['Europe/Budapest'],
  'SI': ['Europe/Ljubljana'],
  'HR': ['Europe/Zagreb'],
  'BA': ['Europe/Sarajevo'],
  'ME': ['Europe/Podgorica'],
  'AL': ['Europe/Tirane'],
  'MK': ['Europe/Skopje'],
  'RS': ['Europe/Belgrade'],
  'RO': ['Europe/Bucharest'],
  'BG': ['Europe/Sofia'],
  'GR': ['Europe/Athens'],
  'TR': ['Europe/Istanbul'],
  'CY': ['Asia/Nicosia', 'Asia/Famagusta'],
  'RU': ['Europe/Moscow', 'Europe/Kaliningrad', 'Europe/Simferopol', 'Europe/Kirov', 'Europe/Volgograd', 'Europe/Astrakhan', 'Europe/Saratov', 'Europe/Ulyanovsk', 'Europe/Samara', 'Asia/Yekaterinburg', 'Asia/Omsk', 'Asia/Novosibirsk', 'Asia/Barnaul', 'Asia/Tomsk', 'Asia/Novokuznetsk', 'Asia/Krasnoyarsk', 'Asia/Irkutsk', 'Asia/Chita', 'Asia/Yakutsk', 'Asia/Khandyga', 'Asia/Vladivostok', 'Asia/Ust-Nera', 'Asia/Magadan', 'Asia/Sakhalin', 'Asia/Srednekolymsk', 'Asia/Kamchatka', 'Asia/Anadyr'],
  'UA': ['Europe/Kiev', 'Europe/Uzhgorod', 'Europe/Zaporozhye'],
  'BY': ['Europe/Minsk'],
  'MD': ['Europe/Chisinau'],
  'ZA': ['Africa/Johannesburg'],
  'EG': ['Africa/Cairo'],
  'MA': ['Africa/Casablanca'],
  'DZ': ['Africa/Algiers'],
  'TN': ['Africa/Tunis'],
  'LY': ['Africa/Tripoli'],
  'KE': ['Africa/Nairobi'],
  'ET': ['Africa/Addis_Ababa'],
  'NG': ['Africa/Lagos'],
  'GH': ['Africa/Accra'],
  'SN': ['Africa/Dakar'],
  'IN': ['Asia/Kolkata'],
  'CN': ['Asia/Shanghai', 'Asia/Urumqi', 'Asia/Kashgar'],
  'JP': ['Asia/Tokyo'],
  'KR': ['Asia/Seoul'],
  'TW': ['Asia/Taipei'],
  'HK': ['Asia/Hong_Kong'],
  'MO': ['Asia/Macau'],
  'PH': ['Asia/Manila'],
  'MY': ['Asia/Kuala_Lumpur', 'Asia/Kuching'],
  'SG': ['Asia/Singapore'],
  'ID': ['Asia/Jakarta', 'Asia/Pontianak', 'Asia/Makassar', 'Asia/Jayapura'],
  'TH': ['Asia/Bangkok'],
  'VN': ['Asia/Ho_Chi_Minh'],
  'KH': ['Asia/Phnom_Penh'],
  'LA': ['Asia/Vientiane'],
  'MM': ['Asia/Yangon'],
  'BD': ['Asia/Dhaka'],
  'NP': ['Asia/Kathmandu'],
  'PK': ['Asia/Karachi'],
  'AF': ['Asia/Kabul'],
  'IR': ['Asia/Tehran'],
  'IQ': ['Asia/Baghdad'],
  'SA': ['Asia/Riyadh'],
  'YE': ['Asia/Aden'],
  'AE': ['Asia/Dubai'],
  'OM': ['Asia/Muscat'],
  'BH': ['Asia/Bahrain'],
  'QA': ['Asia/Qatar'],
  'KW': ['Asia/Kuwait'],
  'IL': ['Asia/Jerusalem'],
  'JO': ['Asia/Amman'],
  'LB': ['Asia/Beirut'],
  'SY': ['Asia/Damascus'],
  'AU': ['Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Adelaide', 'Australia/Darwin', 'Australia/Perth', 'Australia/Hobart', 'Australia/Eucla', 'Australia/Lord_Howe'],
  'NZ': ['Pacific/Auckland', 'Pacific/Chatham'],
  'FJ': ['Pacific/Fiji'],
};

// Fallback for countries not in our map
export const defaultTimezones = ['UTC'];

// Format timezone for display
export const formatTimezone = (timezone: string): string => {
  const now = new Date();
  // Specify the correct type for timeZoneName as "long" with as const
  const options = { timeZoneName: "long" as const, timeZone: timezone };
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(now);
  const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value || timezone;
  
  try {
    // Same for the second DateTimeFormat, use "short" as const
    const offset = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: "short" as const })
      .formatToParts(now)
      .find(part => part.type === 'timeZoneName')?.value || '';
    
    return `${timezone.replace('_', ' ').replace(/\//g, ' / ')} (${timeZoneName} ${offset})`;
  } catch (error) {
    return timezone;
  }
};

// List of countries for the dropdown
export const countries = [
  { name: 'Afghanistan', code: 'AF' },
  { name: 'Albania', code: 'AL' },
  { name: 'Algeria', code: 'DZ' },
  { name: 'Andorra', code: 'AD' },
  { name: 'Angola', code: 'AO' },
  { name: 'Antigua and Barbuda', code: 'AG' },
  { name: 'Argentina', code: 'AR' },
  { name: 'Armenia', code: 'AM' },
  { name: 'Australia', code: 'AU' },
  { name: 'Austria', code: 'AT' },
  { name: 'Azerbaijan', code: 'AZ' },
  { name: 'Bahamas', code: 'BS' },
  { name: 'Bahrain', code: 'BH' },
  { name: 'Bangladesh', code: 'BD' },
  { name: 'Barbados', code: 'BB' },
  { name: 'Belarus', code: 'BY' },
  { name: 'Belgium', code: 'BE' },
  { name: 'Belize', code: 'BZ' },
  { name: 'Benin', code: 'BJ' },
  { name: 'Bhutan', code: 'BT' },
  { name: 'Bolivia', code: 'BO' },
  { name: 'Bosnia and Herzegovina', code: 'BA' },
  { name: 'Botswana', code: 'BW' },
  { name: 'Brazil', code: 'BR' },
  { name: 'Brunei', code: 'BN' },
  { name: 'Bulgaria', code: 'BG' },
  { name: 'Burkina Faso', code: 'BF' },
  { name: 'Burundi', code: 'BI' },
  { name: 'Cabo Verde', code: 'CV' },
  { name: 'Cambodia', code: 'KH' },
  { name: 'Cameroon', code: 'CM' },
  { name: 'Canada', code: 'CA' },
  { name: 'Central African Republic', code: 'CF' },
  { name: 'Chad', code: 'TD' },
  { name: 'Chile', code: 'CL' },
  { name: 'China', code: 'CN' },
  { name: 'Colombia', code: 'CO' },
  { name: 'Comoros', code: 'KM' },
  { name: 'Congo', code: 'CG' },
  { name: 'Costa Rica', code: 'CR' },
  { name: 'Croatia', code: 'HR' },
  { name: 'Cuba', code: 'CU' },
  { name: 'Cyprus', code: 'CY' },
  { name: 'Czech Republic', code: 'CZ' },
  { name: 'Denmark', code: 'DK' },
  { name: 'Djibouti', code: 'DJ' },
  { name: 'Dominica', code: 'DM' },
  { name: 'Dominican Republic', code: 'DO' },
  { name: 'Ecuador', code: 'EC' },
  { name: 'Egypt', code: 'EG' },
  { name: 'El Salvador', code: 'SV' },
  { name: 'Equatorial Guinea', code: 'GQ' },
  { name: 'Eritrea', code: 'ER' },
  { name: 'Estonia', code: 'EE' },
  { name: 'Eswatini', code: 'SZ' },
  { name: 'Ethiopia', code: 'ET' },
  { name: 'Fiji', code: 'FJ' },
  { name: 'Finland', code: 'FI' },
  { name: 'France', code: 'FR' },
  { name: 'Gabon', code: 'GA' },
  { name: 'Gambia', code: 'GM' },
  { name: 'Georgia', code: 'GE' },
  { name: 'Germany', code: 'DE' },
  { name: 'Ghana', code: 'GH' },
  { name: 'Greece', code: 'GR' },
  { name: 'Grenada', code: 'GD' },
  { name: 'Guatemala', code: 'GT' },
  { name: 'Guinea', code: 'GN' },
  { name: 'Guinea-Bissau', code: 'GW' },
  { name: 'Guyana', code: 'GY' },
  { name: 'Haiti', code: 'HT' },
  { name: 'Honduras', code: 'HN' },
  { name: 'Hungary', code: 'HU' },
  { name: 'Iceland', code: 'IS' },
  { name: 'India', code: 'IN' },
  { name: 'Indonesia', code: 'ID' },
  { name: 'Iran', code: 'IR' },
  { name: 'Iraq', code: 'IQ' },
  { name: 'Ireland', code: 'IE' },
  { name: 'Israel', code: 'IL' },
  { name: 'Italy', code: 'IT' },
  { name: 'Jamaica', code: 'JM' },
  { name: 'Japan', code: 'JP' },
  { name: 'Jordan', code: 'JO' },
  { name: 'Kazakhstan', code: 'KZ' },
  { name: 'Kenya', code: 'KE' },
  { name: 'Kiribati', code: 'KI' },
  { name: 'Korea, North', code: 'KP' },
  { name: 'Korea, South', code: 'KR' },
  { name: 'Kosovo', code: 'XK' },
  { name: 'Kuwait', code: 'KW' },
  { name: 'Kyrgyzstan', code: 'KG' },
  { name: 'Laos', code: 'LA' },
  { name: 'Latvia', code: 'LV' },
  { name: 'Lebanon', code: 'LB' },
  { name: 'Lesotho', code: 'LS' },
  { name: 'Liberia', code: 'LR' },
  { name: 'Libya', code: 'LY' },
  { name: 'Liechtenstein', code: 'LI' },
  { name: 'Lithuania', code: 'LT' },
  { name: 'Luxembourg', code: 'LU' },
  { name: 'Madagascar', code: 'MG' },
  { name: 'Malawi', code: 'MW' },
  { name: 'Malaysia', code: 'MY' },
  { name: 'Maldives', code: 'MV' },
  { name: 'Mali', code: 'ML' },
  { name: 'Malta', code: 'MT' },
  { name: 'Marshall Islands', code: 'MH' },
  { name: 'Mauritania', code: 'MR' },
  { name: 'Mauritius', code: 'MU' },
  { name: 'Mexico', code: 'MX' },
  { name: 'Micronesia', code: 'FM' },
  { name: 'Moldova', code: 'MD' },
  { name: 'Monaco', code: 'MC' },
  { name: 'Mongolia', code: 'MN' },
  { name: 'Montenegro', code: 'ME' },
  { name: 'Morocco', code: 'MA' },
  { name: 'Mozambique', code: 'MZ' },
  { name: 'Myanmar', code: 'MM' },
  { name: 'Namibia', code: 'NA' },
  { name: 'Nauru', code: 'NR' },
  { name: 'Nepal', code: 'NP' },
  { name: 'Netherlands', code: 'NL' },
  { name: 'New Zealand', code: 'NZ' },
  { name: 'Nicaragua', code: 'NI' },
  { name: 'Niger', code: 'NE' },
  { name: 'Nigeria', code: 'NG' },
  { name: 'North Macedonia', code: 'MK' },
  { name: 'Norway', code: 'NO' },
  { name: 'Oman', code: 'OM' },
  { name: 'Pakistan', code: 'PK' },
  { name: 'Palau', code: 'PW' },
  { name: 'Palestine', code: 'PS' },
  { name: 'Panama', code: 'PA' },
  { name: 'Papua New Guinea', code: 'PG' },
  { name: 'Paraguay', code: 'PY' },
  { name: 'Peru', code: 'PE' },
  { name: 'Philippines', code: 'PH' },
  { name: 'Poland', code: 'PL' },
  { name: 'Portugal', code: 'PT' },
  { name: 'Qatar', code: 'QA' },
  { name: 'Romania', code: 'RO' },
  { name: 'Russia', code: 'RU' },
  { name: 'Rwanda', code: 'RW' },
  { name: 'Saint Kitts and Nevis', code: 'KN' },
  { name: 'Saint Lucia', code: 'LC' },
  { name: 'Saint Vincent and the Grenadines', code: 'VC' },
  { name: 'Samoa', code: 'WS' },
  { name: 'San Marino', code: 'SM' },
  { name: 'Sao Tome and Principe', code: 'ST' },
  { name: 'Saudi Arabia', code: 'SA' },
  { name: 'Senegal', code: 'SN' },
  { name: 'Serbia', code: 'RS' },
  { name: 'Seychelles', code: 'SC' },
  { name: 'Sierra Leone', code: 'SL' },
  { name: 'Singapore', code: 'SG' },
  { name: 'Slovakia', code: 'SK' },
  { name: 'Slovenia', code: 'SI' },
  { name: 'Solomon Islands', code: 'SB' },
  { name: 'Somalia', code: 'SO' },
  { name: 'South Africa', code: 'ZA' },
  { name: 'South Sudan', code: 'SS' },
  { name: 'Spain', code: 'ES' },
  { name: 'Sri Lanka', code: 'LK' },
  { name: 'Sudan', code: 'SD' },
  { name: 'Suriname', code: 'SR' },
  { name: 'Sweden', code: 'SE' },
  { name: 'Switzerland', code: 'CH' },
  { name: 'Syria', code: 'SY' },
  { name: 'Taiwan', code: 'TW' },
  { name: 'Tajikistan', code: 'TJ' },
  { name: 'Tanzania', code: 'TZ' },
  { name: 'Thailand', code: 'TH' },
  { name: 'Timor-Leste', code: 'TL' },
  { name: 'Togo', code: 'TG' },
  { name: 'Tonga', code: 'TO' },
  { name: 'Trinidad and Tobago', code: 'TT' },
  { name: 'Tunisia', code: 'TN' },
  { name: 'Turkey', code: 'TR' },
  { name: 'Turkmenistan', code: 'TM' },
  { name: 'Tuvalu', code: 'TV' },
  { name: 'Uganda', code: 'UG' },
  { name: 'Ukraine', code: 'UA' },
  { name: 'United Arab Emirates', code: 'AE' },
  { name: 'United Kingdom', code: 'GB' },
  { name: 'United States', code: 'US' },
  { name: 'Uruguay', code: 'UY' },
  { name: 'Uzbekistan', code: 'UZ' },
  { name: 'Vanuatu', code: 'VU' },
  { name: 'Vatican City', code: 'VA' },
  { name: 'Venezuela', code: 'VE' },
  { name: 'Vietnam', code: 'VN' },
  { name: 'Yemen', code: 'YE' },
  { name: 'Zambia', code: 'ZM' },
  { name: 'Zimbabwe', code: 'ZW' },
];

// List of countries for the dropdown
export const countries_fr = [
  { name: 'Afghanistan', code: 'AF' },
  { name: 'Albanie', code: 'AL' },
  { name: 'Algérie', code: 'DZ' },
  { name: 'Andorre', code: 'AD' },
  { name: 'Angola', code: 'AO' },
  { name: 'Antigua-et-Barbuda', code: 'AG' },
  { name: 'Argentine', code: 'AR' },
  { name: 'Arménie', code: 'AM' },
  { name: 'Australie', code: 'AU' },
  { name: 'Autriche', code: 'AT' },
  { name: 'Azerbaïdjan', code: 'AZ' },
  { name: 'Bahamas', code: 'BS' },
  { name: 'Bahreïn', code: 'BH' },
  { name: 'Bangladesh', code: 'BD' },
  { name: 'Barbade', code: 'BB' },
  { name: 'Biélorussie', code: 'BY' },
  { name: 'Belgique', code: 'BE' },
  { name: 'Belize', code: 'BZ' },
  { name: 'Bénin', code: 'BJ' },
  { name: 'Bhoutan', code: 'BT' },
  { name: 'Bolivie', code: 'BO' },
  { name: 'Bosnie-Herzégovine', code: 'BA' },
  { name: 'Botswana', code: 'BW' },
  { name: 'Brésil', code: 'BR' },
  { name: 'Brunéi', code: 'BN' },
  { name: 'Bulgarie', code: 'BG' },
  { name: 'Burkina Faso', code: 'BF' },
  { name: 'Burundi', code: 'BI' },
  { name: 'Cabo Verde', code: 'CV' },
  { name: 'Cambodge', code: 'KH' },
  { name: 'Cameroun', code: 'CM' },
  { name: 'Canada', code: 'CA' },
  { name: 'République Centrafricaine', code: 'CF' },
  { name: 'Tchad', code: 'TD' },
  { name: 'Chili', code: 'CL' },
  { name: 'Chine', code: 'CN' },
  { name: 'Colombie', code: 'CO' },
  { name: 'Comores', code: 'KM' },
  { name: 'Congo', code: 'CG' },
  { name: 'Costa Rica', code: 'CR' },
  { name: 'Croatie', code: 'HR' },
  { name: 'Cuba', code: 'CU' },
  { name: 'Chypre', code: 'CY' },
  { name: 'République Tchèque', code: 'CZ' },
  { name: 'Danemark', code: 'DK' },
  { name: 'Djibouti', code: 'DJ' },
  { name: 'Dominique', code: 'DM' },
  { name: 'République Dominicaine', code: 'DO' },
  { name: 'Équateur', code: 'EC' },
  { name: 'Égypte', code: 'EG' },
  { name: 'Salvador', code: 'SV' },
  { name: 'Guinée Équatoriale', code: 'GQ' },
  { name: 'Érythrée', code: 'ER' },
  { name: 'Estonie', code: 'EE' },
  { name: 'Eswatini', code: 'SZ' },
  { name: 'Éthiopie', code: 'ET' },
  { name: 'Fidji', code: 'FJ' },
  { name: 'Finlande', code: 'FI' },
  { name: 'France', code: 'FR' },
  { name: 'Gabon', code: 'GA' },
  { name: 'Gambie', code: 'GM' },
  { name: 'Géorgie', code: 'GE' },
  { name: 'Allemagne', code: 'DE' },
  { name: 'Ghana', code: 'GH' },
  { name: 'Grèce', code: 'GR' },
  { name: 'Grenade', code: 'GD' },
  { name: 'Guatemala', code: 'GT' },
  { name: 'Guinée', code: 'GN' },
  { name: 'Guinée-Bissau', code: 'GW' },
  { name: 'Guyana', code: 'GY' },
  { name: 'Haïti', code: 'HT' },
  { name: 'Honduras', code: 'HN' },
  { name: 'Hongrie', code: 'HU' },
  { name: 'Islande', code: 'IS' },
  { name: 'Inde', code: 'IN' },
  { name: 'Indonésie', code: 'ID' },
  { name: 'Iran', code: 'IR' },
  { name: 'Irak', code: 'IQ' },
  { name: 'Irlande', code: 'IE' },
  { name: 'Israël', code: 'IL' },
  { name: 'Italie', code: 'IT' },
  { name: 'Jamaïque', code: 'JM' },
  { name: 'Japon', code: 'JP' },
  { name: 'Jordanie', code: 'JO' },
  { name: 'Kazakhstan', code: 'KZ' },
  { name: 'Kenya', code: 'KE' },
  { name: 'Kiribati', code: 'KI' },
  { name: 'Corée du Nord', code: 'KP' },
  { name: 'Corée du Sud', code: 'KR' },
  { name: 'Kosovo', code: 'XK' },
  { name: 'Koweït', code: 'KW' },
  { name: 'Kirghizistan', code: 'KG' },
  { name: 'Laos', code: 'LA' },
  { name: 'Lettonie', code: 'LV' },
  { name: 'Liban', code: 'LB' },
  { name: 'Lesotho', code: 'LS' },
  { name: 'Libéria', code: 'LR' },
  { name: 'Libye', code: 'LY' },
  { name: 'Liechtenstein', code: 'LI' },
  { name: 'Lituanie', code: 'LT' },
  { name: 'Luxembourg', code: 'LU' },
  { name: 'Madagascar', code: 'MG' },
  { name: 'Malawi', code: 'MW' },
  { name: 'Malaisie', code: 'MY' },
  { name: 'Maldives', code: 'MV' },
  { name: 'Mali', code: 'ML' },
  { name: 'Malte', code: 'MT' },
  { name: 'Îles Marshall', code: 'MH' },
  { name: 'Mauritanie', code: 'MR' },
  { name: 'Maurice', code: 'MU' },
  { name: 'Mexique', code: 'MX' },
  { name: 'Micronésie', code: 'FM' },
  { name: 'Moldavie', code: 'MD' },
  { name: 'Monaco', code: 'MC' },
  { name: 'Mongolie', code: 'MN' },
  { name: 'Monténégro', code: 'ME' },
  { name: 'Maroc', code: 'MA' },
  { name: 'Mozambique', code: 'MZ' },
  { name: 'Myanmar', code: 'MM' },
  { name: 'Namibie', code: 'NA' },
  { name: 'Nauru', code: 'NR' },
  { name: 'Népal', code: 'NP' },
  { name: 'Pays-Bas', code: 'NL' },
  { name: 'Nouvelle-Zélande', code: 'NZ' },
  { name: 'Nicaragua', code: 'NI' },
  { name: 'Niger', code: 'NE' },
  { name: 'Nigéria', code: 'NG' },
  { name: 'Macédoine du Nord', code: 'MK' },
  { name: 'Norvège', code: 'NO' },
  { name: 'Oman', code: 'OM' },
  { name: 'Pakistan', code: 'PK' },
  { name: 'Palaos', code: 'PW' },
  { name: 'Palestine', code: 'PS' },
  { name: 'Panama', code: 'PA' },
  { name: 'Papouasie-Nouvelle-Guinée', code: 'PG' },
  { name: 'Paraguay', code: 'PY' },
  { name: 'Pérou', code: 'PE' },
  { name: 'Philippines', code: 'PH' },
  { name: 'Pologne', code: 'PL' },
  { name: 'Portugal', code: 'PT' },
  { name: 'Qatar', code: 'QA' },
  { name: 'Roumanie', code: 'RO' },
  { name: 'Russie', code: 'RU' },
  { name: 'Rwanda', code: 'RW' },
  { name: 'Saint-Christophe-et-Niévès', code: 'KN' },
  { name: 'Sainte-Lucie', code: 'LC' },
  { name: 'Saint-Vincent-et-les Grenadines', code: 'VC' },
  { name: 'Samoa', code: 'WS' },
  { name: 'Saint-Marin', code: 'SM' },
  { name: 'Sao Tomé-et-Principe', code: 'ST' },
  { name: 'Arabie Saoudite', code: 'SA' },
  { name: 'Sénégal', code: 'SN' },
  { name: 'Serbie', code: 'RS' },
  { name: 'Seychelles', code: 'SC' },
  { name: 'Sierra Leone', code: 'SL' },
  { name: 'Singapour', code: 'SG' },
  { name: 'Slovaquie', code: 'SK' },
  { name: 'Slovénie', code: 'SI' },
  { name: 'Îles Salomon', code: 'SB' },
  { name: 'Somalie', code: 'SO' },
  { name: 'Afrique du Sud', code: 'ZA' },
  { name: 'Soudan du Sud', code: 'SS' },
  { name: 'Espagne', code: 'ES' },
  { name: 'Sri Lanka', code: 'LK' },
  { name: 'Soudan', code: 'SD' },
  { name: 'Suriname', code: 'SR' },
  { name: 'Suède', code: 'SE' },
  { name: 'Suisse', code: 'CH' },
  { name: 'Syrie', code: 'SY' },
  { name: 'Taïwan', code: 'TW' },
  { name: 'Tadjikistan', code: 'TJ' },
  { name: 'Tanzanie', code: 'TZ' },
  { name: 'Thaïlande', code: 'TH' },
  { name: 'Timor-Leste', code: 'TL' },
  { name: 'Togo', code: 'TG' },
  { name: 'Tonga', code: 'TO' },
  { name: 'Trinité-et-Tobago', code: 'TT' },
  { name: 'Tunisie', code: 'TN' },
  { name: 'Turquie', code: 'TR' },
  { name: 'Turkménistan', code: 'TM' },
  { name: 'Tuvalu', code: 'TV' },
  { name: 'Ouganda', code: 'UG' },
  { name: 'Ukraine', code: 'UA' },
  { name: 'Émirats Arabes Unis', code: 'AE' },
  { name: 'Royaume-Uni', code: 'GB' },
  { name: 'États-Unis', code: 'US' },
  { name: 'Uruguay', code: 'UY' },
  { name: 'Ouzbékistan', code: 'UZ' },
  { name: 'Vanuatu', code: 'VU' },
  { name: 'Cité du Vatican', code: 'VA' },
  { name: 'Venezuela', code: 'VE' },
  { name: 'Vietnam', code: 'VN' },
  { name: 'Yémen', code: 'YE' },
  { name: 'Zambie', code: 'ZM' },
  { name: 'Zimbabwe', code: 'ZW' },
];

export const currencies = [
  { code: 'USD', name: 'US Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'RUB', name: 'Russian Ruble' },
  { code: 'ZAR', name: 'South African Rand' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'HKD', name: 'Hong Kong Dollar' },
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'TRY', name: 'Turkish Lira' },
  { code: 'KRW', name: 'South Korean Won' },
  { code: 'AED', name: 'United Arab Emirates Dirham' },
  { code: 'SAR', name: 'Saudi Riyal' },
  { code: 'THB', name: 'Thai Baht' },
  { code: 'IDR', name: 'Indonesian Rupiah' },
  { code: 'MYR', name: 'Malaysian Ringgit' },
  { code: 'PHP', name: 'Philippine Peso' },
  { code: 'DKK', name: 'Danish Krone' },
  { code: 'PLN', name: 'Polish Zloty' },
  { code: 'HUF', name: 'Hungarian Forint' },
  { code: 'CZK', name: 'Czech Koruna' },
  { code: 'ILS', name: 'Israeli New Shekel' },
  { code: 'CLP', name: 'Chilean Peso' },
  { code: 'COP', name: 'Colombian Peso' },
  { code: 'PEN', name: 'Peruvian Sol' },
  { code: 'ARS', name: 'Argentine Peso' },
  { code: 'EGP', name: 'Egyptian Pound' },
  { code: 'NGN', name: 'Nigerian Naira' },
  { code: 'KES', name: 'Kenyan Shilling' },
  { code: 'VND', name: 'Vietnamese Dong' },
  { code: 'TWD', name: 'New Taiwan Dollar' },
  { code: 'UAH', name: 'Ukrainian Hryvnia' },
  { code: 'RON', name: 'Romanian Leu' },
  { code: 'BDT', name: 'Bangladeshi Taka' },
  { code: 'PKR', name: 'Pakistani Rupee' },
  { code: 'LKR', name: 'Sri Lankan Rupee' },
  { code: 'NPR', name: 'Nepalese Rupee' },
  { code: 'KWD', name: 'Kuwaiti Dinar' },
  { code: 'QAR', name: 'Qatari Riyal' },
  { code: 'OMR', name: 'Omani Rial' },
  { code: 'JOD', name: 'Jordanian Dinar' },
  { code: 'BHD', name: 'Bahraini Dinar' },
  { code: 'KZT', name: 'Kazakhstani Tenge' },
  { code: 'UZS', name: 'Uzbekistani Som' },
  { code: 'BYN', name: 'Belarusian Ruble' },
  { code: 'AZN', name: 'Azerbaijani Manat' },
  { code: 'GEL', name: 'Georgian Lari' },
  { code: 'AMD', name: 'Armenian Dram' },
  { code: 'MAD', name: 'Moroccan Dirham' },
  { code: 'DZD', name: 'Algerian Dinar' },
  { code: 'TND', name: 'Tunisian Dinar' },
  { code: 'LYD', name: 'Libyan Dinar' },
  { code: 'ETB', name: 'Ethiopian Birr' },
  { code: 'GHS', name: 'Ghanaian Cedi' },
  { code: 'UGX', name: 'Ugandan Shilling' },
  { code: 'TZS', name: 'Tanzanian Shilling' },
  { code: 'RWF', name: 'Rwandan Franc' },
  { code: 'BWP', name: 'Botswana Pula' },
  { code: 'ZMW', name: 'Zambian Kwacha' },
  { code: 'MWK', name: 'Malawian Kwacha' },
  { code: 'MUR', name: 'Mauritian Rupee' },
  { code: 'SCR', name: 'Seychellois Rupee' },
  { code: 'BIF', name: 'Burundian Franc' },
  { code: 'XOF', name: 'West African CFA Franc' },
  { code: 'XAF', name: 'Central African CFA Franc' },
  { code: 'XPF', name: 'CFP Franc' },
  { code: 'DJF', name: 'Djiboutian Franc' },
  { code: 'SOS', name: 'Somali Shilling' },
  { code: 'SLL', name: 'Sierra Leonean Leone' },
  { code: 'LRD', name: 'Liberian Dollar' },
  { code: 'GMD', name: 'Gambian Dalasi' },
  { code: 'GNF', name: 'Guinean Franc' },
  { code: 'CDF', name: 'Congolese Franc' },
  { code: 'AOA', name: 'Angolan Kwanza' },
  { code: 'MZN', name: 'Mozambican Metical' },
  { code: 'SZL', name: 'Swazi Lilangeni' },
  { code: 'LSL', name: 'Lesotho Loti' },
  { code: 'NAD', name: 'Namibian Dollar' },
  { code: 'BND', name: 'Brunei Dollar' },
  { code: 'FJD', name: 'Fijian Dollar' },
  { code: 'PGK', name: 'Papua New Guinean Kina' },
  { code: 'WST', name: 'Samoan Tala' },
  { code: 'TOP', name: 'Tongan Pa\'anga' },
  { code: 'VUV', name: 'Vanuatu Vatu' },
  { code: 'SBD', name: 'Solomon Islands Dollar' },
  { code: 'KID', name: 'Kiribati Dollar' },
  { code: 'TVD', name: 'Tuvaluan Dollar' },
  { code: 'BBD', name: 'Barbadian Dollar' },
  { code: 'BMD', name: 'Bermudian Dollar' },
  { code: 'BZD', name: 'Belize Dollar' },
  { code: 'KYD', name: 'Cayman Islands Dollar' },
  { code: 'XCD', name: 'East Caribbean Dollar' },
  { code: 'FKP', name: 'Falkland Islands Pound' },
  { code: 'GIP', name: 'Gibraltar Pound' },
  { code: 'GGY', name: 'Guernsey Pound' },
  { code: 'JEP', name: 'Jersey Pound' },
  { code: 'IMP', name: 'Isle of Man Pound' },
  { code: 'LBP', name: 'Lebanese Pound' },
  { code: 'SRD', name: 'Surinamese Dollar' },
  { code: 'TTD', name: 'Trinidad and Tobago Dollar' },
  { code: 'YER', name: 'Yemeni Rial' },
  { code: 'IQD', name: 'Iraqi Dinar' },
  { code: 'IRR', name: 'Iranian Rial' },
  { code: 'AFN', name: 'Afghan Afghani' },
  { code: 'ALL', name: 'Albanian Lek' },
  { code: 'MKD', name: 'Macedonian Denar' },
  { code: 'RSD', name: 'Serbian Dinar' },
  { code: 'BGN', name: 'Bulgarian Lev' },
  { code: 'HRK', name: 'Croatian Kuna' },
  { code: 'ISK', name: 'Icelandic Krona' },
];